import { Component, Inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
  FormBuilder,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserService } from 'src/app/core/api/user/user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { PromotionalTextDTO } from 'src/app/shared/models/dtos/responses/promotional-text-dto/promotional-text-dto';
import { MatDialogResponse } from '../../../../shared/models/constants/mat-dialog-response';

@Component({
  selector: 'app-promo-text-modal',
  templateUrl: './promo-text-modal.component.html',
  styleUrls: ['./promo-text-modal.component.scss'],
})
/**
 * Modal for edit promotional vip texts
 */
export class PromoTextModalComponent {
  form: FormGroup = new FormGroup({
    en: new FormControl('', [Validators.maxLength(255)]),
    es: new FormControl('', [Validators.maxLength(255)]),
    pt: new FormControl('', [Validators.maxLength(255)]),
  });

  labels = {
    saveSuccessMsg: 'promo-text-dialog.successMsg',
    noChangesDetectedMsg: 'promo-text-dialog.noChangesDetectedMsg',
  };

  /**
   * Constructor
   */
  constructor(
    private dialogRef: MatDialogRef<PromoTextModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PromotionalTextDTO,
    private readonly userService: UserService,
    private readonly snackBarService: SnackBarService,
    private readonly translationService: TranslationService,
    private fb: FormBuilder
  ) {
    this.translationService.translateObject(this.labels);
    this.form = this.fb.group(
      {
        en: [data.promotionalTextEnglish],
        es: [data.promotionalTextSpanish],
        pt: [data.promotionalTextPortuguese],
      },
      { validators: [this.allOrNoneValidator] }
    );
  }

  /**
   * retrieves the form control associated with the specified language.
   * @param {string} lang
   * @return {FormControl}
   */
  getControlByLang(lang: string): FormControl {
    return this.form.get(lang) as FormControl;
  }

  /**
   * Save
   */
  clickSave(): void {
    const requestBody = {
      series: this.data.series,
      promotionalTextEnglish: this.form.get(['en'].join('.'))!.getRawValue(),
      promotionalTextPortuguese: this.form.get(['pt'].join('.'))!.getRawValue(),
      promotionalTextSpanish: this.form.get(['es'].join('.'))!.getRawValue(),
      active: true,
      familyPrimaryKey: this.data.familyId,
    };

    // Detect any change on promotional texts
    if (this.checkIfAnyChangesWereMade(requestBody)) {
      this.updateVipSeriesConfig(requestBody);
    } else {
      this.snackBarService.neutral(this.labels.noChangesDetectedMsg);
    }
  }

  /**
   * Check if any change was made on the form fields
   * @param {any} newData the new data
   * @return {boolean} true if any change was made
   */
  checkIfAnyChangesWereMade(newData: any): boolean {
    return (
      this.data.promotionalTextEnglish != newData['promotionalTextEnglish'] ||
      this.data.promotionalTextSpanish != newData['promotionalTextSpanish'] ||
      this.data.promotionalTextPortuguese !=
        newData['promotionalTextPortuguese']
    );
  }

  /**
   * Update one Vip configuration for a specific series on back-end (User Management Service)
   * @param {any} requestBody request body
   */
  updateVipSeriesConfig(requestBody: any): void {
    this.userService.updateVipConfigSeries(requestBody).subscribe(() => {
      this.dialogRef.close(MatDialogResponse.SUCCESS);
      this.snackBarService.success(this.labels.saveSuccessMsg);
    });
  }

  /**
   * Cancel button click action
   */
  clickCancel(): void {
    this.dialogRef.close();
  }

  /**
   * custom validator that checks if all fields in a group are filled when any one is.
   * @param {AbstractControl} group
   * @return {ValidationErrors | null}
   */
  allOrNoneValidator(group: AbstractControl): ValidationErrors | null {
    const formGroup = group as FormGroup;
    const controls = formGroup.controls;

    const values = Object.values(controls).map((c) => c.value?.trim());
    const filledCount = values.filter((v) => v !== '').length;

    Object.values(controls).forEach((control) => {
      if (control.hasError('allOrNone')) {
        const errors = { ...control.errors };
        delete errors['allOrNone'];
        control.setErrors(Object.keys(errors).length ? errors : null);
      }
    });

    if (filledCount > 0 && filledCount < values.length) {
      Object.entries(controls).forEach(([_key, control]) => {
        if (!control.value?.trim()) {
          control.setErrors({ ...(control.errors || {}), allOrNone: true });
          control.markAsTouched();
        }
      });
    }

    return null;
  }
}
